import type {
  ColumnPinningPosition,
  ColumnPinningState,
  Column,
  RowData,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { type JSX, useCallback, useMemo, useRef } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import { Grid } from '../../../../../layouts/grid/Grid.js';
import { screenReaderOnly } from '../../../../../styles/screen-reader-only.sty.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import { getCanHide } from '../../../features/ColumnVisibility/ColumnVisibility.js';
import type { TDataTableFeatures } from '../../../features/types/features.types.js';
import { dataTableMessages } from '../../../messages.js';
import type {
  ColumnListItem,
  ColumnSettingsListItem,
} from './column-settings-types.js';
import {
  generateColumnLabelsMap,
  getVisibilityState,
  groupColumns,
  isParentColumnHideable,
  processFilterValue,
} from './column-settings-utils.js';
import { DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT } from './ColumnOrderSettings/column-order-settings-dnd-utils.js';
import { ColumnOrderSettingsDragAndDropContext } from './ColumnOrderSettings/ColumnOrderSettingsDragAndDrop.js';
import * as styles from './DataTableColumnsList.sty.js';
import { columnName } from './DataTableColumnsListItem.sty.js';
import { DataTableColumnsListItem } from './DataTableColumnsListItem.js';
import { DataTableColumnsSectionHeader } from './DataTableColumnsSectionHeader.js';
import { DataTableNoFilterResultsEmptyState } from './DataTableNoFilterResultsEmptyState.js';

interface DataTableColumnsListProps {
  /**
   * Whether the visibility settings (toggles) are enabled.
   */
  visibilitySettings: boolean;
  /**
   * Whether the column order settings (drag and drop) are enabled.
   */
  columnOrderSettings: boolean;
  /**
   * Whether the column pinning settings (pin/unpin buttons) are enabled.
   */
  columnPinningSettings: boolean;
  /**
   * Current value of the filter input in the column settings modal.
   */
  activeFilterValue: string;
  /**
   * Whether hidden columns are shown in the list.
   * @defaultValue true
   */
  listHiddenColumns?: boolean;
  /**
   * Current local column visibility state in the column settings modal.
   */
  localColumnVisibility: Record<string, boolean>;
  /**
   * Updates the local column visibility state in the column settings modal.
   * @param column - the column to update.
   * @param currentVisibility - the current visibility state of the column.
   */
  updateLocalColumnVisibility: (
    column: Column<TDataTableFeatures, RowData>,
    currentVisibility: boolean | 'indeterminate',
  ) => void;
  /**
   * Current local column order in the column settings modal.
   */
  localColumnOrder: string[];
  /**
   * Updates the local column order in the column settings modal.
   * @param newColumnOrder - the new column order.
   */
  updateLocalColumnOrder: (newColumnOrder: string[]) => void;
  /**
   * Current local column pinning state in the column settings modal.
   */
  localColumnPinning: ColumnPinningState;
  /**
   * Updates the local column pinning state in the column settings modal.
   * @param newState - the new column pinning state.
   */
  updateLocalColumnPinning: (newState: ColumnPinningState) => void;
  /**
   * Whether the filter bar is present in the modal above the list.
   */
  hasFilterBar: boolean;
  /**
   * Whether the visibility controls (switch + column counter) are shown above the list.
   */
  hasVisibilityControl: boolean;
  /**
   * Whether a warning or error message is shown in the modal footer.
   */
  hasMessage: boolean;
  /**
   * Callback to clear the active search filter.
   */
  onClearFilter: () => void;
}

/**
 * Builds the screen-reader announcement for the current search outcome.
 * Reports the number of leaf columns matching the current filter value.
 * Nothing is announced when no filter is active.
 * @param listItems - the current list items shown in the column settings panel.
 * @param filterValueLength - length of the active filter string; 0 means no filter is active.
 * @returns the live-region message
 */
function getFilterResultAnnouncement(
  listItems: ColumnSettingsListItem<TDataTableFeatures, RowData>[],
  filterValueLength: number,
  hasFilterBar: boolean,
  intl: IntlShape,
): string {
  if (!hasFilterBar) {
    return '';
  }

  const count = listItems.filter(
    (item) => item.itemType === 'column' && item.columns.length === 0,
  ).length;

  if (filterValueLength === 0) {
    return intl.formatMessage(dataTableMessages.showingAllColumns, { count });
  }

  return count === 0
    ? intl.formatMessage(dataTableMessages.noFilterResultsTitle)
    : intl.formatMessage(dataTableMessages.filterResultsCount, { count });
}

/**
 * Renders the ordered, filtered list of columns for the column settings modal.
 * @internal
 */
export function DataTableColumnsList(
  props: Readonly<DataTableColumnsListProps>,
): JSX.Element | JSX.Element[] {
  const {
    visibilitySettings: showVisibilityToggle,
    columnOrderSettings,
    columnPinningSettings,
    activeFilterValue,
    listHiddenColumns = true,
    localColumnVisibility,
    updateLocalColumnVisibility,
    localColumnOrder,
    updateLocalColumnOrder,
    localColumnPinning,
    updateLocalColumnPinning,
    hasFilterBar,
    hasVisibilityControl,
    hasMessage,
    onClearFilter,
  } = props;
  const intl = useIntl();
  const table = useDataTable();
  const parentRef = useRef<HTMLDivElement | null>(null);

  const allLeafColumnsWithoutBuiltin = table.getAllLeafColumnsWithoutBuiltin();
  const columnsById = useMemo(
    () => new Map(allLeafColumnsWithoutBuiltin.map((col) => [col.id, col])),
    [allLeafColumnsWithoutBuiltin],
  );

  // Memoize column labels to avoid repeated getColumnLabel calls during filtering
  const columnLabelsById = useMemo(
    () => generateColumnLabelsMap(allLeafColumnsWithoutBuiltin),
    [allLeafColumnsWithoutBuiltin],
  );

  // Pre-process filter value to lowercase words once
  const activeFilterWords = useMemo(
    () => processFilterValue(activeFilterValue),
    [activeFilterValue],
  );

  // The comparator below runs for every pair of columns, so looking positions
  // up in a map keeps sorting fast even with a few thousand columns.
  const columnOrderIndexById = useMemo(() => {
    const indexById = new Map<string, number>();
    for (const [index, columnId] of localColumnOrder.entries()) {
      indexById.set(columnId, index);
    }
    return indexById;
  }, [localColumnOrder]);

  // order the columns using the local column order
  const compareColumns = useCallback(
    (
      a: Column<TDataTableFeatures, RowData>,
      b: Column<TDataTableFeatures, RowData>,
    ) => {
      const idChildColumnA = a.columns.at(0)?.id;
      const idChildColumnB = b.columns.at(0)?.id;
      const isBChildOfA = !!a.columns.find((childCol) => childCol.id === b.id);
      const isAChildOfB = !!b.columns.find((childCol) => childCol.id === a.id);

      if (isBChildOfA) {
        return -1;
      } else if (isAChildOfB) {
        return 1;
      }

      // `?? -1` mirrors the not-found result of the previous `indexOf` lookup.
      return (
        (columnOrderIndexById.get(idChildColumnA ?? a.id) ?? -1) -
        (columnOrderIndexById.get(idChildColumnB ?? b.id) ?? -1)
      );
    },
    [columnOrderIndexById],
  );

  // filter the flat columns using the active filter value specified in the search bar
  const filterColumns = useCallback(
    (
      column: ColumnListItem<TDataTableFeatures, RowData>,
      _: number,
      columnSection: ColumnListItem<TDataTableFeatures, RowData>[],
    ): boolean => {
      // When listHiddenColumns is false, exclude hidden columns from the list.
      if (!listHiddenColumns) {
        const childCols = column.columns;
        if (childCols.length === 0) {
          if (!localColumnVisibility[column.id]) {
            return false;
          }
        } else {
          // Group column: hide if all children are hidden
          const hasVisibleChild = childCols.some(
            (c) => localColumnVisibility[c.id],
          );
          if (!hasVisibleChild) {
            return false;
          }
        }
      }

      // Early return if no text filter
      if (activeFilterWords.length === 0) {
        return true;
      }

      // Helper function to check if a column matches the filter words
      const matchesFilter = (columnId: string): boolean => {
        const columnLabel = columnLabelsById.get(columnId) ?? '';
        return activeFilterWords.every((word) => columnLabel.includes(word));
      };

      // a column always matches on its own label, regardless of nesting
      if (matchesFilter(column.id)) {
        return true;
      }

      const childColumns = column.columns;
      if (childColumns.length === 0) {
        // leaf column: also match its parent group label
        // so the children stay visible when the parent group name is searched.
        return column.parent !== undefined && matchesFilter(column.parent.id);
      }

      // parent (grouped) column: match any of its children
      // within the current (un)pinned section.
      return childColumns
        .filter((child) => columnSection.find((col) => col.id === child.id))
        .some((childColumn) => matchesFilter(childColumn.id));
    },
    [
      activeFilterWords,
      columnLabelsById,
      listHiddenColumns,
      localColumnVisibility,
    ],
  );

  const mapToListItem = (
    col: Column<TDataTableFeatures, RowData>,
    idx: number,
    pinnedPosition: ColumnPinningPosition,
  ): ColumnListItem<TDataTableFeatures, RowData> & { itemType: 'column' } => ({
    ...col,
    key: `${col.id}_${idx}_${pinnedPosition}`, // unique React key; parent columns may appear multiple times in list
    pinned: pinnedPosition,
    itemType: 'column',
  });

  const orderedLeftFlatColumns: ColumnListItem<TDataTableFeatures, RowData>[] =
    useMemo(() => {
      // map column IDs to columns
      const localColumnsPinned =
        localColumnPinning.start
          ?.map((id) => columnsById.get(id))
          .filter((column) => !!column) ?? [];

      // group nested columns
      const groupedColumns = groupColumns(localColumnsPinned);

      // add unique key
      return groupedColumns.map((col, idx) => mapToListItem(col, idx, 'start'));
    }, [columnsById, localColumnPinning.start]);

  const orderedCenterFlatColumns: ColumnListItem<
    TDataTableFeatures,
    RowData
  >[] = useMemo(() => {
    // find unpinned columns
    const localColumnsUnpinned = allLeafColumnsWithoutBuiltin.filter(
      (column) =>
        !localColumnPinning.start?.includes(column.id) &&
        !localColumnPinning.end?.includes(column.id),
    );

    // group nested columns
    const groupedColumns = groupColumns(localColumnsUnpinned);

    // order columns and add unique key
    return groupedColumns
      .toSorted(compareColumns)
      .map((col, idx) => mapToListItem(col, idx, false));
  }, [
    allLeafColumnsWithoutBuiltin,
    compareColumns,
    localColumnPinning.start,
    localColumnPinning.end,
  ]);

  const orderedRightFlatColumns: ColumnListItem<TDataTableFeatures, RowData>[] =
    useMemo(() => {
      // map column IDs to columns
      const localColumnsPinned =
        localColumnPinning.end
          ?.map((id) => columnsById.get(id))
          .filter((column) => !!column) ?? [];

      // group nested columns
      const groupedColumns = groupColumns(localColumnsPinned);

      // add unique key
      return groupedColumns.map((col, idx) => mapToListItem(col, idx, 'end'));
    }, [columnsById, localColumnPinning.end]);

  const orderedFlatColumns = {
    left: orderedLeftFlatColumns,
    center: orderedCenterFlatColumns,
    right: orderedRightFlatColumns,
  };

  const showColumnPinningButton =
    !!table.options.enableColumnPinning && columnPinningSettings;

  // Section headers are only meaningful when pinning is available and at least
  // one column is actually pinned; otherwise the list is a single ungrouped section.
  const showSectionHeaders =
    showColumnPinningButton &&
    (orderedLeftFlatColumns.length > 0 || orderedRightFlatColumns.length > 0);

  // Flat, virtualized list of items: each section's columns, optionally preceded
  // by a section header. Empty sections (e.g. filtered out) are skipped.
  const listItems = useMemo<
    ColumnSettingsListItem<TDataTableFeatures, RowData>[]
  >(() => {
    const sections: {
      position: ColumnPinningPosition;
      columns: ColumnListItem<TDataTableFeatures, RowData>[];
    }[] = [
      {
        position: 'start',
        columns: orderedLeftFlatColumns.filter(filterColumns),
      },
      {
        position: 'end',
        columns: orderedRightFlatColumns.filter(filterColumns),
      },
      {
        position: false, // = center
        columns: orderedCenterFlatColumns.filter(filterColumns),
      },
    ];

    const items: ColumnSettingsListItem<TDataTableFeatures, RowData>[] = [];
    for (const section of sections) {
      if (section.columns.length === 0) {
        continue;
      }
      if (showSectionHeaders) {
        items.push({
          itemType: 'sectionHeader',
          position: section.position,
          key: `__section_header_${section.position}`,
        });
      }
      items.push(
        ...(section.columns as ColumnSettingsListItem<
          TDataTableFeatures,
          RowData
        >[]),
      );
    }
    return items;
  }, [
    filterColumns,
    orderedCenterFlatColumns,
    orderedLeftFlatColumns,
    orderedRightFlatColumns,
    showSectionHeaders,
  ]);

  const getItemKey = useCallback(
    (index: number) => listItems[index].key,
    [listItems],
  );

  const columnVirtualizer = useVirtualizer({
    count: listItems.length,
    overscan: 5,
    getScrollElement: () => parentRef.current,
    getItemKey,
    // Approximate height of a column list item
    estimateSize: () => DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT,
    measureElement: (element) => {
      if (!element || !(element instanceof HTMLElement)) {
        return DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT;
      }

      const labelElement = element.querySelector(`.${columnName}`);
      if (!labelElement) {
        // section headers (and any non-column items) are measured by their own height
        return (
          element.getBoundingClientRect().height ||
          DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT
        );
      }

      const computedStyle = getComputedStyle(element);
      const borderTop = parseFloat(computedStyle.borderTopWidth);
      const borderBottom = parseFloat(computedStyle.borderBottomWidth);

      return (
        labelElement.getBoundingClientRect().height +
        2 * 4 + // padding top and bottom
        borderTop +
        borderBottom
      );
    },
  });

  const virtualItems = columnVirtualizer.getVirtualItems();

  const filterResultAnnouncement = getFilterResultAnnouncement(
    listItems,
    activeFilterValue.length,
    hasFilterBar,
    intl,
  );

  const liveRegion = (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={screenReaderOnly}
    >
      {filterResultAnnouncement}
    </div>
  );

  // fallback if no columns match the filter
  if (listItems.length === 0) {
    return (
      <>
        {liveRegion}
        <DataTableNoFilterResultsEmptyState onClearSearch={onClearFilter} />
      </>
    );
  }

  const canOrder =
    !!table.options.enableColumnOrdering &&
    columnOrderSettings &&
    activeFilterValue.length === 0;

  const columnListItems = virtualItems.map((virtualItem) => {
    const column = listItems[virtualItem.index];

    if (column.itemType === 'sectionHeader') {
      return (
        <DataTableColumnsSectionHeader
          key={column.key}
          position={column.position}
          virtualIndex={virtualItem.index}
          measureElement={columnVirtualizer.measureElement}
        />
      );
    }

    const columnKey = column.key;
    const nextItem = listItems[virtualItem.index + 1];
    const isLastInSection =
      nextItem === undefined || nextItem.itemType === 'sectionHeader';
    const canHide = getCanHide(table, column, isParentColumnHideable);
    const visibilityState = getVisibilityState(localColumnVisibility, column);
    const showColumnOrderDragHandle = canOrder && column.pinned === false;

    return (
      <DataTableColumnsListItem
        key={columnKey}
        column={column}
        columnIndex={columnOrderIndexById.get(column.id) ?? -1}
        filterWords={activeFilterWords}
        orderedFlatColumns={orderedFlatColumns}
        showVisibilityToggle={showVisibilityToggle}
        enableVisibilityToggle={canHide}
        currentVisibilityState={visibilityState}
        onVisibilityChange={updateLocalColumnVisibility}
        showColumnOrderDragHandle={showColumnOrderDragHandle}
        showColumnPinningMenu={showColumnPinningButton}
        localColumnPinning={localColumnPinning}
        updateLocalColumnPinning={updateLocalColumnPinning}
        isLastInSection={isLastInSection}
        virtualIndex={virtualItem.index}
        measureElement={columnVirtualizer.measureElement}
      />
    );
  });

  const columnList = (
    <div
      ref={parentRef}
      className={clsx(
        'strato-table-settings-virtualization-container',
        styles.virtualizationScrollContainer({
          hasFilter: hasFilterBar,
          hasVisibilityControl,
          hasMessage,
          hasPinnedColumns: listItems[0].itemType === 'sectionHeader',
        }),
      )}
    >
      <Grid
        gridTemplateColumns="1fr"
        padding={0}
        gap={0}
        className={clsx(styles.virtualizationInnerContainer)}
        // Array.from is used instead of .map() because measurementsCache can be sparse, causing gaps between options.
        gridTemplateRows={Array.from(
          columnVirtualizer.measurementsCache,
          (el) => `${el?.size ?? DATA_TABLE_COLUMNS_LIST_ITEM_MIN_HEIGHT}px`,
        ).join(' ')}
        // add 1px to prevent unwanted scrollbar during drag and drop
        height={columnVirtualizer.getTotalSize() + 1}
      >
        {columnListItems}
      </Grid>
    </div>
  );

  return (
    <>
      {liveRegion}
      {canOrder ? (
        <ColumnOrderSettingsDragAndDropContext
          localColumnOrder={localColumnOrder}
          updateLocalColumnOrder={updateLocalColumnOrder}
          localColumnPinning={localColumnPinning}
          itemVirtualizer={columnVirtualizer}
        >
          {columnList}
        </ColumnOrderSettingsDragAndDropContext>
      ) : (
        columnList
      )}
    </>
  );
}
